import { Anchor, SegmentedControl, Table, Text } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { api, podatki } from '../api/odjemalec';
import { casIzMs } from '../oblika/oblika';
import { GlavaZaslona, Seznam, Stolpec, ZaslonSeznama } from '../seznam/Seznam';
import { useSeznam } from '../seznam/useSeznam';
import { Oznaka } from '../strani/PregledRacunov';
import { stanjePovprasevanja } from './povprasevanja';
import { VklopObvestil } from './VklopObvestil';

const STANJA = [
  { value: 'odprta', label: 'Odprta' },
  { value: 'nova', label: 'Nova' },
  { value: 'resena', label: 'Rešena' },
  { value: 'vsa', label: 'Vsa' },
];

/**
 * Povpraševanja obiskovalcev javne ponudbe vozil (novo v spletu, Swing ga nima): privzeto odprta (nova in prebrana),
 * najnovejša najprej, strani na strežniku, stanje v URL-ju. Klik odpre podrobnosti; zgoraj vklop push obvestil.
 */
export function PregledPovprasevanj() {
  const seznam = useSeznam();
  const navigate = useNavigate();
  const stanje = seznam.parameter('stanje') ?? 'odprta';
  const poizvedba = useQuery({
    queryKey: ['povprasevanja', stanje, seznam.zaApi],
    queryFn: ({ signal }) =>
      podatki(
        api.GET('/api/povprasevanja', {
          signal,
          params: { query: { stanje, stran: seznam.zaApi.stran, velikost: seznam.zaApi.velikost } },
        }),
      ),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
  const vrstice = poizvedba.data?.vsebina ?? [];

  return (
    <ZaslonSeznama>
      <GlavaZaslona
        naslov="Povpraševanja"
        opis="Povpraševanja kupcev z vaše javne ponudbe vozil."
        pomoc="povprasevanja"
        dejanja={<VklopObvestil />}
      />
      <Seznam
        seznam={seznam}
        filtri={
          <SegmentedControl
            aria-label="Stanje"
            data={STANJA}
            value={stanje}
            onChange={(v) => seznam.nastavi({ stanje: v === 'odprta' ? null : v })}
          />
        }
        glava={
          <>
            <Stolpec seznam={seznam} naziv="Ime" />
            <Stolpec seznam={seznam} naziv="Vozilo" />
            <Stolpec seznam={seznam} naziv="Stanje" />
            <Stolpec seznam={seznam} naziv="Prejeto" />
            <Stolpec seznam={seznam} naziv="Kontakt" />
            <Stolpec seznam={seznam} naziv="Sporočilo" />
          </>
        }
        vrstice={vrstice.map((p, i) => {
          const s = stanjePovprasevanja(p.stanje);
          return (
            <Table.Tr
              key={p.id}
              style={{ cursor: 'pointer', fontWeight: p.stanje === 'NOVO' ? 700 : undefined }}
              onClick={() => void navigate(`/povprasevanja/${p.id}`)}
            >
              <Table.Td c="dimmed" className="brez-preloma">
                {seznam.zamik + i + 1}
              </Table.Td>
              <Table.Td className="brez-preloma">
                <Anchor component={Link} to={`/povprasevanja/${p.id}`} onClick={(e) => e.stopPropagation()} fw={600}>
                  {p.ime}
                </Anchor>
              </Table.Td>
              <Table.Td>{p.voziloNaziv ?? <Text c="dimmed">splošno</Text>}</Table.Td>
              <Table.Td>
                <Oznaka barva={s.barva}>{s.naziv}</Oznaka>
              </Table.Td>
              <Table.Td className="brez-preloma">{casIzMs(p.ustvarjeno)}</Table.Td>
              <Table.Td className="brez-preloma">{[p.telefon, p.email].filter(Boolean).join(' · ')}</Table.Td>
              <Table.Td maw={320}>
                <Text size="sm" truncate>
                  {p.sporocilo}
                </Text>
              </Table.Td>
            </Table.Tr>
          );
        })}
        stVrstic={vrstice.length}
        stStolpcev={6}
        skupaj={poizvedba.data?.skupaj ?? 0}
        nalaganje={poizvedba.isPending}
        osvezevanje={poizvedba.isFetching}
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        prazno={
          stanje === 'odprta'
            ? 'Ni odprtih povpraševanj. Ko kupec pošlje povpraševanje z vaše javne ponudbe, se prikaže tukaj.'
            : 'Ni povpraševanj.'
        }
        napakaNaslov="Povpraševanj ni bilo mogoče naložiti"
      />
    </ZaslonSeznama>
  );
}
